/**
 * Hair Matters — ShopVid (Avada) video gallery: fast facade + smart load
 *
 * Videos stay on the page. We make them feel fast by:
 * 1) Intercepting cdn-shoppable.avada.io until the widget is needed
 * 2) Painting real video posters immediately from window.SHOPPABLE_VIDEO
 *    (already in the HTML) so the gallery isn't blank white
 * 3) Warming ShopVid JS on idle + loading early when the section is near
 *    (large rootMargin), so interactivity is ready by the time users arrive
 *
 * Does not touch Judge.me. Pages without ShopVid never load the ~0.9MB bundle.
 */
(function () {
  'use strict';

  var LOG = '[hm-shopvid]';
  var SCRIPT_RE = /cdn-shoppable\.avada\.io|shoppable-video(?:\.min)?\.js/i;
  var ROOT_SEL = '.ShopVid-Shoppable, .mp-tiktok-feed, [id*="shopvid_mp_tiktok"]';
  // Start ShopVid JS ~1.5 viewports before the section is visible
  var ROOT_MARGIN_PX = 1200;
  // After LCP-ish quiet period, warm scripts so scroll feels instant
  var IDLE_WARM_MS = 2500;
  var POSTER_COUNT = 4;
  var POSTER_WIDTH = 400;
  var DESIGN_MODE =
    (window.Shopify && Shopify.designMode) ||
    document.documentElement.classList.contains('shopify-design-mode');
  var IS_HOME =
    document.body && document.body.classList.contains('template-index');

  var queued = [];
  var released = false;
  var stylesReady = false;
  var origCreateElement = document.createElement.bind(document);

  function log() {
    if (typeof console !== 'undefined' && console.log) {
      console.log.apply(console, [LOG].concat([].slice.call(arguments)));
    }
  }

  function nowMs() {
    return Math.round(
      (window.performance && performance.now && performance.now()) || Date.now()
    );
  }

  function matchesShopvidScript(src) {
    return !!(src && SCRIPT_RE.test(src));
  }

  function preconnectCdn() {
    if (document.querySelector('link[data-hm-shopvid-preconnect]')) return;
    var link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = 'https://cdn-shoppable.avada.io';
    link.crossOrigin = 'anonymous';
    link.setAttribute('data-hm-shopvid-preconnect', '1');
    (document.head || document.documentElement).appendChild(link);

    var dns = document.createElement('link');
    dns.rel = 'dns-prefetch';
    dns.href = 'https://cdn-shoppable.avada.io';
    dns.setAttribute('data-hm-shopvid-preconnect', '1');
    (document.head || document.documentElement).appendChild(dns);
  }

  function shopifySizedUrl(url, width) {
    if (!url || typeof url !== 'string') return url;
    if (url.indexOf('cdn.shopify.com') === -1) return url;
    if (/([?&])width=\d+/.test(url)) {
      return url.replace(/([?&])width=\d+/, '$1width=' + width);
    }
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function getPosterVideos() {
    var data = window.SHOPPABLE_VIDEO;
    if (!data || !data.videos || !data.videos.length) return [];
    var out = [];
    for (var i = 0; i < data.videos.length && out.length < POSTER_COUNT; i++) {
      var v = data.videos[i];
      if (!v) continue;
      var src = v.thumbnailPreview || v.socialThumbnail || v.thumbnail || '';
      if (!src) continue;
      out.push({
        src: shopifySizedUrl(src, POSTER_WIDTH),
        alt: (v.fileName || 'Product video').replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ')
      });
    }
    return out;
  }

  function injectStyles() {
    if (stylesReady) return;
    stylesReady = true;
    var style = document.createElement('style');
    style.id = 'hm-shopvid-defer-css';
    style.textContent =
      '.ShopVid-Shoppable.hm-shopvid-facade,' +
      '.mp-tiktok-feed .ShopVid-Shoppable.hm-shopvid-facade{' +
      'min-height:388px!important;padding-block:24px;box-sizing:border-box}' +
      '.hm-shopvid-facade-row{display:flex;gap:16px;justify-content:center;' +
      'align-items:stretch;overflow:hidden;padding:8px 12px 16px;box-sizing:border-box}' +
      '.hm-shopvid-facade-card{flex:1 1 0;max-width:280px;aspect-ratio:9/16;' +
      'border-radius:12px;overflow:hidden;position:relative;background:#e8e8e8;' +
      'box-shadow:0 1px 2px rgba(0,0,0,.06)}' +
      '.hm-shopvid-facade-card img{width:100%;height:100%;object-fit:cover;display:block;' +
      'background:#e8e8e8}' +
      '.hm-shopvid-facade-card--empty{' +
      'background:linear-gradient(90deg,#ececec 25%,#f6f6f6 37%,#ececec 63%);' +
      'background-size:400% 100%;animation:hm-shopvid-shimmer 1.4s ease infinite}' +
      '.hm-shopvid-facade-play{position:absolute;inset:0;margin:auto;width:56px;height:56px;' +
      'border-radius:50%;background:rgba(255,255,255,.82);' +
      'box-shadow:0 2px 8px rgba(0,0,0,.18);pointer-events:none}' +
      '.hm-shopvid-facade-play::after{content:"";position:absolute;left:52%;top:50%;' +
      'transform:translate(-40%,-50%);width:0;height:0;' +
      'border-style:solid;border-width:10px 0 10px 16px;' +
      'border-color:transparent transparent transparent #222}' +
      '@keyframes hm-shopvid-shimmer{0%{background-position:100% 0}100%{background-position:0 0}}' +
      '@media (max-width:749px){.hm-shopvid-facade-row{gap:10px;padding-inline:8px}' +
      '.hm-shopvid-facade-card{max-width:42vw}}';
    (document.head || document.documentElement).appendChild(style);
  }

  function showFacade(root) {
    if (!root) return;
    injectStyles();
    preconnectCdn();

    var host =
      root.classList.contains('ShopVid-Shoppable')
        ? root
        : root.querySelector('.ShopVid-Shoppable') || root;
    if (!host || host.querySelector('.hm-shopvid-facade-row')) return;
    if (host.classList.contains('_mpLoaded')) return;

    host.classList.add('hm-shopvid-facade');
    var mount = host.querySelector('.ShopVid-WidgetRoot') || host;
    var row = document.createElement('div');
    row.className = 'hm-shopvid-facade-row';
    row.setAttribute('aria-hidden', 'true');
    row.setAttribute('data-hm-shopvid-facade', '1');

    var posters = getPosterVideos();
    var count = Math.max(posters.length, 3);
    for (var i = 0; i < count && i < POSTER_COUNT; i++) {
      var card = document.createElement('div');
      card.className = 'hm-shopvid-facade-card';
      if (posters[i]) {
        var img = document.createElement('img');
        img.src = posters[i].src;
        img.alt = posters[i].alt || '';
        img.decoding = 'async';
        img.loading = i === 0 ? 'eager' : 'lazy';
        img.fetchPriority = i === 0 ? 'low' : 'auto';
        card.appendChild(img);
      } else {
        card.className += ' hm-shopvid-facade-card--empty';
      }
      var play = document.createElement('span');
      play.className = 'hm-shopvid-facade-play';
      card.appendChild(play);
      row.appendChild(card);
    }
    mount.appendChild(row);
    log('facade posters=' + posters.length + ' at T=' + nowMs() + 'ms');
  }

  function hideFacade(root) {
    if (!root) return;
    var hosts = [];
    if (root.classList && root.classList.contains('ShopVid-Shoppable')) hosts.push(root);
    root.querySelectorAll &&
      root.querySelectorAll('.ShopVid-Shoppable.hm-shopvid-facade').forEach(function (el) {
        hosts.push(el);
      });
    if (!hosts.length && root.classList) hosts.push(root);

    hosts.forEach(function (host) {
      host.classList.remove('hm-shopvid-facade');
      var row = host.querySelector('[data-hm-shopvid-facade]');
      if (row && row.parentNode) row.parentNode.removeChild(row);
    });
  }

  function watchLoaded(root) {
    if (!root || root.getAttribute('data-hm-shopvid-watch') === '1') return;
    root.setAttribute('data-hm-shopvid-watch', '1');

    function maybeHide() {
      var loaded =
        root.classList.contains('_mpLoaded') ||
        !!root.querySelector('.ShopVid-Shoppable._mpLoaded, ._mpLoaded .Shopvid__VideoBlock');
      if (loaded) {
        hideFacade(root);
        return true;
      }
      return false;
    }

    if (maybeHide()) return;

    var obs = new MutationObserver(function () {
      if (maybeHide()) obs.disconnect();
    });
    obs.observe(root, { attributes: true, attributeFilter: ['class'], childList: true, subtree: true });
    setTimeout(maybeHide, 20000);
  }

  function releaseScripts(reason) {
    if (released) return;
    released = true;
    document.createElement = origCreateElement;
    log('releasing ShopVid scripts at T=' + nowMs() + 'ms (' + reason + '), queued=' + queued.length);

    for (var i = 0; i < queued.length; i++) {
      var q = queued[i];
      var s = origCreateElement('script');
      s.async = q.async !== false;
      if (q.type) s.type = q.type;
      s.src = q.src;
      (document.head || document.documentElement).appendChild(s);
    }
    queued = [];
  }

  // Intercept ShopVid CDN scripts created by mp-tiktok-feed.min.js
  document.createElement = function (tagName) {
    var el = origCreateElement.apply(document, arguments);
    if (String(tagName).toLowerCase() !== 'script') return el;

    var realSrc = '';
    Object.defineProperty(el, 'src', {
      configurable: true,
      enumerable: true,
      get: function () {
        return realSrc;
      },
      set: function (value) {
        realSrc = value || '';
        if (!released && matchesShopvidScript(realSrc)) {
          el.setAttribute('data-hm-shopvid', '1');
          el.setAttribute('data-hm-src', realSrc);
          queued.push({ src: realSrc, async: true, type: el.type || 'text/javascript' });
          log('queued', realSrc.replace(/^https?:\/\//, '').slice(0, 80), 'at T=' + nowMs() + 'ms');
          return;
        }
        el.setAttribute('src', realSrc);
      }
    });
    return el;
  };

  function findRoots() {
    return Array.prototype.slice.call(document.querySelectorAll(ROOT_SEL));
  }

  function isNearViewport(el, margin) {
    if (!el || typeof el.getBoundingClientRect !== 'function') return false;
    margin = typeof margin === 'number' ? margin : ROOT_MARGIN_PX;
    var measureEl = el;
    var rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      measureEl = el.closest('.shopify-section') || el;
      rect = measureEl.getBoundingClientRect();
    }
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    return rect.top < vh + margin && rect.bottom > -margin;
  }

  function observeForRelease(root) {
    if (!root || root.getAttribute('data-hm-shopvid-io') === '1') return;
    root.setAttribute('data-hm-shopvid-io', '1');

    var target = root.closest('.shopify-section') || root;

    if (DESIGN_MODE || IS_HOME) {
      releaseScripts(DESIGN_MODE ? 'design-mode' : 'homepage');
      return;
    }

    // Only auto-release when already in the first viewport (no margin).
    // Early layout is unreliable during parse — this runs after DOMContentLoaded.
    if (document.readyState !== 'loading' && isNearViewport(root, 0)) {
      releaseScripts('in-viewport');
      return;
    }

    if (typeof IntersectionObserver === 'function') {
      var io = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) {
              io.disconnect();
              releaseScripts('intersection');
              return;
            }
          }
        },
        { root: null, rootMargin: ROOT_MARGIN_PX + 'px 0px', threshold: 0 }
      );
      io.observe(target);
    } else {
      function onScroll() {
        if (isNearViewport(root)) {
          window.removeEventListener('scroll', onScroll, { passive: true });
          releaseScripts('scroll');
        }
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  }

  function scheduleIdleWarm() {
    if (DESIGN_MODE || released) return;
    if (!findRoots().length) return;

    function warm() {
      if (released) return;
      releaseScripts('idle-warm');
    }

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(
        function () {
          setTimeout(warm, IDLE_WARM_MS);
        },
        { timeout: IDLE_WARM_MS + 2000 }
      );
    } else {
      setTimeout(warm, IDLE_WARM_MS + 1500);
    }
  }

  function tryFacadeFromData(root) {
    // SHOPPABLE_VIDEO is assigned by an inline app-embed script during parse.
    // Retry briefly in case our MO runs a tick before that script.
    showFacade(root);
    if (getPosterVideos().length) return;
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      if (getPosterVideos().length || tries > 20) {
        clearInterval(timer);
        // Rebuild facade once data arrives (replace empty shimmer cards)
        var host =
          root.classList.contains('ShopVid-Shoppable')
            ? root
            : root.querySelector('.ShopVid-Shoppable') || root;
        if (host && !host.classList.contains('_mpLoaded') && getPosterVideos().length) {
          var old = host.querySelector('[data-hm-shopvid-facade]');
          if (old && old.parentNode) old.parentNode.removeChild(old);
          host.classList.remove('hm-shopvid-facade');
          showFacade(root);
        }
      }
    }, 50);
  }

  function armRoot(root) {
    if (!root || root.getAttribute('data-hm-shopvid-armed') === '1') return;
    root.setAttribute('data-hm-shopvid-armed', '1');
    tryFacadeFromData(root);
    watchLoaded(root);
    if (document.readyState !== 'loading') {
      observeForRelease(root);
    }
  }

  function scanAndObserve() {
    var roots = findRoots();
    if (!roots.length) {
      log('no ShopVid root on page — scripts stay deferred');
      return;
    }
    for (var i = 0; i < roots.length; i++) {
      armRoot(roots[i]);
      observeForRelease(roots[i]);
    }
    scheduleIdleWarm();
  }

  var mo = new MutationObserver(function () {
    if (released) {
      mo.disconnect();
      return;
    }
    var roots = findRoots();
    for (var i = 0; i < roots.length; i++) armRoot(roots[i]);
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

  function onReady() {
    scanAndObserve();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  // Last-resort fallback so the gallery never stays stuck on the facade
  setTimeout(function () {
    if (released) return;
    if (findRoots().length) releaseScripts('timeout-fallback');
  }, 20000);
})();
